import React, { useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Button, Input, message, Modal, Table, Radio } from 'antd';
import { connect } from 'react-redux';
import { ColumnsType } from 'antd/es/table';
import { get } from 'lodash';
import { SearchOutlined } from '@ant-design/icons';
import intl from 'react-intl-universal';

import { useGroupCurrentEmployee } from '@/redux/actions/user';
import { storage } from '@/utils';
import { TenantSignStorage } from '@/utils/intl';
import styles from './ChooseShop.module.scss';

interface OpenConfig {
  defaultShop?: string; // 默认选中的门店
}

export interface ImodalRef {
  openModal?: (config?: OpenConfig) => void;
  openNoPermissionModal?: () => void;
  closeModal?: () => void;
}

const columns: ColumnsType<any> = [
  { title: intl.get('login_choose_shop_col1'), dataIndex: 'commonCode' },
  { title: intl.get('login_choose_shop_col2'), dataIndex: 'shopName' },
];

enum TypeKeyEnum {
  GROUP = 'GROUP', // 集团
  STORE = 'STORE', // 门店
}

export const STATUS: any = [
  { label: intl.get('login_choose_shop_status1'), value: TypeKeyEnum.GROUP },
  { label: intl.get('login_choose_shop_status2'), value: TypeKeyEnum.STORE },
];

const ChooseShop = ({ myRef, shops, companyGroup, onCancel, updateCurrentEmployee }) => {
  const [isNoAuth, setIsNoAuth] = useState<boolean>(false);
  const [visible, setVisible] = useState<boolean>(false);
  const [searchVal, setSearchVal] = useState<string>();
  const [selectedKey, setSelectedKey] = useState<Array<any>>();

  const [loginType, setLoginType] = useState<TypeKeyEnum>(TypeKeyEnum.GROUP);

  const [tabOptions, setTabOptions] = useState([]);

  // 商户名称
  const merchantName = TenantSignStorage.getLocalConfig()?.marchantName || '--';

  const filterList = useMemo(() => {
    if (!searchVal) {
      return shops;
    }
    return shops?.filter(
      (item) => item.commonCode?.includes(searchVal) || item.shopName?.includes(searchVal),
    );
  }, [searchVal, shops]);

  const openModal = () => {
    setVisible(true);
  };

  const closeModal = () => {
    setVisible(false);
  };

  const openNoPermissionModal = () => {
    setVisible(true);
    setIsNoAuth(true);
  };

  const handleOk = () => {
    // 集团登录
    if (loginType === TypeKeyEnum.GROUP) {
      storage.delete('commonCode');
      storage.set('isGroupLogin', TypeKeyEnum.GROUP);

      closeModal();
      setSearchVal('');
      setSelectedKey([]);

      // 更改state.user数据
      updateCurrentEmployee();

      window.location.href = '/room-state/feature-alert-static';
      return;
    }
    // 非集团登录
    if (!Array.isArray(selectedKey) || !selectedKey.length) {
      message.error(intl.get('login_choose_shop_msg1'));
      return;
    }
    storage.set('commonCode', selectedKey[0]);
    storage.delete('isGroupLogin');
    window.location.href = '/room-state/realtime';

    closeModal();
    setSearchVal('');
    setSelectedKey([]);
  };

  const handleClose = () => {
    setIsNoAuth(false);
    onCancel?.();

    closeModal();
    setSearchVal('');
    setSelectedKey([]);
  };

  const onChange = (e) => {
    const { value } = e.target;
    setSearchVal(value);
  };

  // 判断是否有集团权限
  useEffect(() => {
    if (!companyGroup) {
      setLoginType(TypeKeyEnum.STORE);
      setTabOptions([{ label: intl.get('login_choose_shop_label1'), value: TypeKeyEnum.STORE }]);
    } else {
      setLoginType(TypeKeyEnum.GROUP);
      setTabOptions(STATUS);
    }
  }, [companyGroup]);

  useEffect(() => {
    const commonCode = get(filterList, '[0].commonCode');
    if (commonCode) {
      setSelectedKey([commonCode]);
    }
  }, [filterList]);

  const setDefaultShop = (selectCode) => {
    if (selectCode && shops.some((item) => item.commonCode === selectCode)) {
      setSelectedKey([selectCode]);
    }
  };

  useImperativeHandle(
    myRef,
    () => ({
      openModal: (config?: OpenConfig) => {
        setDefaultShop(config?.defaultShop);
        openModal();
      },
      openNoPermissionModal,
      closeModal,
    }),
    [shops],
  );

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedKey(selectedRowKeys);
    },
    getCheckboxProps: (record: any) => ({
      disabled: record.name === 'Disabled User', // Column configuration not to be checked
      name: record.name,
    }),
  };

  return (
    <>
      <Modal
        zIndex={9999}
        title={isNoAuth ? intl.get('login_choose_shop_tit1') : intl.get('login_choose_shop_tit2')}
        visible={visible}
        centered
        maskClosable={false}
        onCancel={handleClose}
        footer={
          isNoAuth ? (
            <Button type="primary" onClick={handleClose}>
              {intl.get('login_choose_shop_btn1')}
            </Button>
          ) : (
            <>
              <Button type="ghost" onClick={handleClose}>
                {intl.get('login_choose_shop_btn2')}
              </Button>
              <Button type="primary" onClick={handleOk}>
                {intl.get('login_choose_shop_btn3')}
              </Button>
            </>
          )
        }
      >
        <Radio.Group
          className={styles.radioTab}
          options={tabOptions}
          onChange={(e) => setLoginType(e.target.value)}
          value={loginType}
          optionType="button"
        />
        {/* 门店登录 */}
        {loginType === TypeKeyEnum.STORE ? (
          <>
            {isNoAuth ? (
              <div>{intl.get('login_choose_shop_label2')}</div>
            ) : (
              <>
                <Input
                  prefix={<SearchOutlined style={{ color: '#969799' }} />}
                  allowClear
                  placeholder={intl.get('login_choose_shop_ph1')}
                  style={{ marginBottom: '16px' }}
                  value={searchVal}
                  onChange={onChange}
                />
                <div style={{ height: '340px', overflow: 'auto' }}>
                  <Table
                    rowKey="commonCode"
                    rowSelection={{ type: 'radio', selectedRowKeys: selectedKey, ...rowSelection }}
                    columns={columns}
                    dataSource={filterList}
                    pagination={false}
                    onRow={(record) => {
                      return {
                        onClick: () => {
                          setSelectedKey([record?.commonCode]);
                        },
                      };
                    }}
                  />
                </div>
              </>
            )}
          </>
        ) : null}
        {/* 集团登录 */}
        {loginType === TypeKeyEnum.GROUP ? (
          <>
            {isNoAuth ? (
              <div>{intl.get('login_choose_shop_label2')}</div>
            ) : (
              <div style={{ height: '373px', marginTop: '15px' }}>
                <Radio checked>{merchantName}</Radio>
              </div>
            )}
          </>
        ) : null}
      </Modal>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    ...state.user,
  };
};

const ConnectComponent: React.FC<any> = connect(mapStateToProps, {
  updateCurrentEmployee: useGroupCurrentEmployee,
})(React.memo(ChooseShop));

export default React.forwardRef<ImodalRef, any>((props, ref) => (
  <ConnectComponent {...props} myRef={ref} />
));
